<template>
    <component
        :is="as"
        :disabled="loading || disabled"
        :key="loading"
        :class="`${colorClasses} ${sizeClasses} ${cursorClasses}`"
        class="tracking-wide shadow-xs uppercase font-bold flex items-center justify-center gap-2 outline-none focus:outline-none ring-0 focus:ring-0"
    >
        <template v-if="loading">
            <Icon
                name="loading-spinner"
                class="animate-spin"
                :class="iconClasses"
            />
        </template>

        <template v-if="iconName && !loading && !iconRight">
            <Icon
                :name="iconName"
                :class="iconClasses"
            />
        </template>

        <template v-if="slots.default || (loading && loadingText)">
            <div v-if="loading && loadingText">{{ loadingText }}</div>
            <div
                v-else-if="slots.default"
                class="whitespace-nowrap"
            >
                <slot name="default" />
            </div>
        </template>

        <template v-if="iconName && !loading && iconRight">
            <Icon
                :name="iconName"
                :class="iconClasses"
            />
        </template>
    </component>
</template>

<script setup lang="ts">
    import { computed, useSlots, type PropType } from 'vue'

    import Icon from '@/Shared/Component/Icon.vue'

    const props = defineProps({
        as: {
            type: String as PropType<'button' | 'a'>,
            default: 'button',
        },
        color: {
            type: String as PropType<
                | 'light'
                | 'dark'
                | 'primary'
                | 'info'
                | 'warning'
                | 'danger'
                | 'outline'
                | 'success'
                | 'danger-outline'
                | 'dark-outline'
                | 'info-outline'
            >,
            default: 'dark',
        },
        size: {
            type: String as PropType<'normal' | 'small'>,
            default: 'normal',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        loadingText: {
            type: String,
            default: null,
        },
        iconName: {
            type: String,
            default: null,
        },
        iconClass: {
            type: String,
            default: null,
        },
        iconRight: {
            type: Boolean,
            default: false,
        },
        useBookingFormThemeCssVariables: {
            type: Boolean,
            default: false,
        },
    })

    const slots: any = useSlots()

    const iconClasses = computed(() => {
        if (props.iconClass) {
            return props.iconClass
        }

        if (props.size == 'small') {
            return 'w-3 h-3'
        }

        return 'w-4 h-4'
    })

    const sizeClasses = computed(() => {
        if (props.size == 'small') {
            return 'px-3 py-1.5 text-xs h-8 rounded-lg'
        }

        return 'px-4 py-2.5 text-sm h-10.5 rounded-lg2'
    })

    const cursorClasses = computed(() => {
        if (props.loading || props.disabled) {
            return 'cursor-not-allowed'
        }

        return 'cursor-pointer'
    })

    const colorClasses = computed(() => {
        if (props.loading || props.disabled) {
            return 'bg-stone-200 text-gray-500 cursor-default border border-transparent'
        }

        if (props.useBookingFormThemeCssVariables) {
            return 'bg-booking-form-theme-main-background-color hover:bg-booking-form-theme-main-background-color-90 text-booking-form-theme-main-text-shade'
        }

        if (props.color == 'danger') {
            return 'bg-red-600 hover:bg-red-700 text-white border border-red-600'
        }

        if (props.color == 'danger-outline') {
            return 'bg-white text-red-600 border border-stone-200 hover:bg-red-600 hover:text-white'
        }

        if (props.color == 'light') {
            return 'bg-white border border-stone-200 hover:bg-stone-100 text-gray-700'
        }

        if (props.color == 'dark') {
            return 'bg-gray-800 border border-gray-700 hover:bg-gray-900 text-gray-50'
        }

        if (props.color == 'dark-outline') {
            return 'bg-white text-gray-700 border border-stone-200 hover:bg-gray-900 hover:text-white'
        }

        if (props.color == 'success') {
            return 'bg-green-600 hover:bg-green-700 text-white'
        }

        if (props.color == 'warning') {
            return 'bg-yellow-500 hover:bg-yellow-400 text-black border border-yellow-500'
        }

        if (props.color == 'info') {
            return 'bg-blue-500 hover:bg-blue-600 text-white border border-blue-500'
        }

        if (props.color == 'info-outline') {
            return 'bg-white hover:bg-blue-500 text-blue-500 hover:text-white border border-blue-500'
        }

        if (props.color == 'outline') {
            return 'bg-white text-primary border border-stone-200 hover:bg-primary hover:text-white'
        }

        if (props.color === 'primary') {
            return 'bg-primary hover:bg-primary-shade text-primary-content border border-primary'
        }
    })
</script>
